import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Eye, Edit, Trash2, Download } from 'lucide-react';
import { shipmentsApi, customersApi } from '../../lib/api';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';
import AdvancedSearch from '../../components/AdvancedSearch';

export default function ShipmentFrozen() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [editForm, setEditForm] = useState({
    customerId: '',
    quantity: '',
    weight: '',
    unitPrice: '',
    arrivalDate: '',
    remarks: '',
  });

  // メイン日付フィルタ（出庫日）
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // 詳細検索フィルタ
  const [advancedFilters, setAdvancedFilters] = useState({
    arrivalDateFrom: '',
    arrivalDateTo: '',
    customerId: '',
    status: '',
  });

  const hasAdvancedFilters = Boolean(
    advancedFilters.arrivalDateFrom || advancedFilters.arrivalDateTo ||
    advancedFilters.customerId || advancedFilters.status
  );

  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customersApi.getAll({ active: 'true' }),
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['shipments', 'frozen', search, dateFrom, dateTo, advancedFilters],
    queryFn: () => shipmentsApi.getFrozen({ 
      search,
      dateFrom,
      dateTo,
      customerId: advancedFilters.customerId,
    }),
    enabled: Boolean(dateFrom && dateTo),
  });

  const deleteMutation = useMutation({
    mutationFn: shipmentsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      toast.success('出庫を削除しました');
    },
    onError: () => toast.error('出庫の削除に失敗しました'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map(id => shipmentsApi.delete(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      toast.success('選択した出庫を削除しました');
    },
    onError: () => toast.error('一括削除に失敗しました'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => shipmentsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      toast.success('出庫を更新しました');
      setIsEditModalOpen(false);
      setSelectedItem(null);
    },
    onError: () => toast.error('出庫の更新に失敗しました'),
  });

  const handleDetail = (row: any) => {
    setSelectedItem(row);
    setIsDetailModalOpen(true);
  };

  const handleEdit = (row: any) => {
    setSelectedItem(row);
    setEditForm({
      customerId: row.shipment?.customerId || '',
      quantity: row.shipment?.quantity?.toString() || '',
      weight: row.shipment?.weight?.toString() || '',
      unitPrice: row.shipment?.unitPrice?.toString() || '',
      arrivalDate: row.shipment?.arrivalDate || '',
      remarks: row.shipment?.remarks || '',
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = (row: any) => {
    if (row.shipment?.status === 'settled') {
      toast.error('精算済みの出庫は削除できません');
      return;
    }
    if (window.confirm('この出庫を削除しますか？在庫が戻ります。')) {
      deleteMutation.mutate(row.shipment.id);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    const items = data?.data?.data || [];
    const settledItems = items.filter((item: any) => 
      ids.includes(item.shipment?.id) && item.shipment?.status === 'settled'
    );
    if (settledItems.length > 0) {
      toast.error('精算済みの出庫が含まれています。精算済みの出庫は削除できません。');
      return;
    }
    if (confirm(`${ids.length}件の出庫を削除しますか？在庫が戻ります。`)) {
      bulkDeleteMutation.mutate(ids);
    }
  };

  const handleSubmitEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;

    updateMutation.mutate({
      id: selectedItem.shipment.id,
      data: {
        quantity: parseInt(editForm.quantity),
        weight: editForm.weight,
        unitPrice: editForm.unitPrice,
        arrivalDate: editForm.arrivalDate,
        remarks: editForm.remarks,
      },
    });
  };

  const handleExportCSV = () => {
    const items = data?.data?.data || [];
    if (items.length === 0) {
      toast.error('エクスポートするデータがありません');
      return;
    }

    const headers = ['LOT', '出庫日', '売り先', '商品名', 'ブランド', '仕入先', '数量', '重量', '売単価', '金額', '着日', '冷蔵庫', '状態'];
    const rows = items.map((item: any) => [
      item.shipment?.shipmentLot || '',
      item.shipment?.shipmentDate || '',
      item.customer?.name || '',
      item.purchase?.productName || '',
      item.brand?.name || '',
      item.supplier?.name || '',
      item.shipment?.quantity || '',
      item.shipment?.weight || '',
      item.shipment?.unitPrice || '',
      (Number(item.shipment?.weight) || 0) * (Number(item.shipment?.unitPrice) || 0),
      item.shipment?.arrivalDate || '',
      item.coldStorage?.name || '',
      item.shipment?.status === 'settled' ? '精算済' : 
      item.shipment?.status === 'pending' ? '精算待ち' : '出庫済',
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `冷凍出庫一覧_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSVをエクスポートしました');
  };

  const formatNumber = (num: any) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toLocaleString('ja-JP');
  };

  const columns = [
    { 
      key: 'shipment.shipmentLot', 
      label: 'LOT', 
      width: '80px',
      render: (row: any) => row.shipment?.shipmentLot || '-',
    },
    { 
      key: 'shipment.shipmentDate', 
      label: '出庫日', 
      width: '100px',
      render: (row: any) => row.shipment?.shipmentDate || '-',
    },
    { 
      key: 'customer.name', 
      label: '売り先',
      render: (row: any) => row.customer?.name || '-',
    },
    { 
      key: 'purchase.productName', 
      label: '商品名',
      render: (row: any) => row.purchase?.productName || '-',
    },
    { 
      key: 'brand.name', 
      label: 'ブランド',
      render: (row: any) => row.brand?.name || '-',
    },
    { 
      key: 'supplier.name', 
      label: '仕入先',
      render: (row: any) => row.supplier?.name || '-',
    },
    { 
      key: 'shipment.quantity', 
      label: '数量',
      width: '70px',
      render: (row: any) => formatNumber(row.shipment?.quantity),
    },
    { 
      key: 'shipment.weight', 
      label: '重量',
      width: '90px',
      render: (row: any) => formatNumber(row.shipment?.weight),
    },
    { 
      key: 'shipment.unitPrice', 
      label: '売単価',
      width: '90px',
      render: (row: any) => `¥${formatNumber(row.shipment?.unitPrice)}`,
    },
    { 
      key: 'amount', 
      label: '金額',
      width: '110px',
      render: (row: any) => {
        const amount = (Number(row.shipment?.weight) || 0) * (Number(row.shipment?.unitPrice) || 0);
        return `¥${formatNumber(amount)}`;
      },
    },
    { 
      key: 'shipment.arrivalDate', 
      label: '着日',
      width: '100px',
      render: (row: any) => row.shipment?.arrivalDate || '-',
    },
    { 
      key: 'coldStorage.name', 
      label: '冷蔵庫',
      render: (row: any) => row.coldStorage?.name || '-',
    },
    {
      key: 'shipment.status',
      label: '状態',
      width: '80px',
      render: (row: any) => {
        const status = row.shipment?.status;
        return (
          <span className={
            status === 'settled' ? 'badge badge-success' :
            status === 'pending' ? 'badge badge-warning' :
            'badge badge-info'
          }>
            {status === 'settled' ? '精算済' : 
             status === 'pending' ? '精算待' : '出庫済'}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: '操作',
      width: '100px',
      render: (row: any) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleDetail(row); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--accent-color)' }}
            title="詳細"
          >
            <Eye size={16} />
          </button>
          {row.shipment?.status !== 'settled' && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
                className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
                style={{ color: 'var(--warning-color)' }}
                title="編集"
              >
                <Edit size={16} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
                className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
                style={{ color: 'var(--error-color)' }}
                title="削除"
              >
                <Trash2 size={16} />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title="冷凍出庫一覧"
        breadcrumbs={[{ label: '出庫管理' }]}
        dateFilterKey="shipments-frozen-date-filter"
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateChange={(from, to) => { setDateFrom(from); setDateTo(to); }}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="商品名で検索..."
        onApply={() => refetch()}
        onAdvancedSearch={() => setIsAdvancedSearchOpen(!isAdvancedSearchOpen)}
        hasAdvancedFilters={hasAdvancedFilters}
        actions={
          <button className="btn btn-outline btn-sm" onClick={handleExportCSV}>
            <Download size={14} />
          </button>
        }
      />

      {isAdvancedSearchOpen && (
        <AdvancedSearch
          dateFilters={[
            { field: 'arrivalDate', label: '着日', from: advancedFilters.arrivalDateFrom, to: advancedFilters.arrivalDateTo },
          ]}
          selectFilters={[
            {
              field: 'customerId',
              label: '売り先',
              value: advancedFilters.customerId,
              options: (customersData?.data?.data || []).map((c: any) => ({ value: c.id, label: c.name })),
            },
            {
              field: 'status',
              label: 'ステータス',
              value: advancedFilters.status,
              options: [
                { value: 'shipped', label: '出庫済' },
                { value: 'pending', label: '精算待' },
                { value: 'settled', label: '精算済' },
              ],
            },
          ]}
          onDateFilterChange={(field, type, value) => {
            const key = `${field}${type === 'from' ? 'From' : 'To'}`;
            setAdvancedFilters({ ...advancedFilters, [key]: value });
          }}
          onSelectFilterChange={(field, value) => {
            setAdvancedFilters({ ...advancedFilters, [field]: value });
          }}
          onClear={() => {
            setAdvancedFilters({ arrivalDateFrom: '', arrivalDateTo: '', customerId: '', status: '' });
          }}
          onSearch={() => refetch()}
        />
      )}

      <DataTable
        columns={columns}
        data={data?.data?.data || []}
        loading={isLoading}
        selectable
        bulkActions={[
          {
            label: '一括削除',
            icon: <Trash2 size={14} />,
            onClick: handleBulkDelete,
            variant: 'danger' as const,
          },
        ]}
        getRowId={(row: any) => row.shipment?.id}
        emptyMessage="冷凍出庫がありません"
        defaultSortKey="shipment.shipmentDate"
        defaultSortOrder="desc"
      />

      {/* Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => { setIsDetailModalOpen(false); setSelectedItem(null); }}
        title="出庫詳細"
        size="lg"
      >
        {selectedItem && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>出庫LOT</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.shipment?.shipmentLot}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>出庫日</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.shipment?.shipmentDate}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>売り先</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.customer?.name}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>商品名</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.purchase?.productName}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>ブランド</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.brand?.name || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>仕入先</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.supplier?.name || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>冷蔵庫</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.coldStorage?.name || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>着日</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.shipment?.arrivalDate || '-'}
                </p>
              </div>
            </div>

            <div className="border-t pt-4" style={{ borderColor: 'var(--border-color)' }}>
              <h4 className="font-medium mb-3" style={{ color: 'var(--text-primary)' }}>出庫情報</h4>
              <div className="grid grid-cols-4 gap-4">
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>数量</p>
                  <p className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                    {formatNumber(selectedItem.shipment?.quantity)} ケース
                  </p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>重量</p>
                  <p className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                    {formatNumber(selectedItem.shipment?.weight)} kg
                  </p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>売単価</p>
                  <p className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                    ¥{formatNumber(selectedItem.shipment?.unitPrice)}
                  </p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>金額</p>
                  <p className="font-bold text-lg" style={{ color: 'var(--success-color)' }}>
                    ¥{formatNumber((Number(selectedItem.shipment?.weight) || 0) * (Number(selectedItem.shipment?.unitPrice) || 0))}
                  </p>
                </div>
              </div>
            </div>

            {selectedItem.shipment?.remarks && (
              <div className="border-t pt-4" style={{ borderColor: 'var(--border-color)' }}>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>備考</p>
                <p style={{ color: 'var(--text-primary)' }}>{selectedItem.shipment.remarks}</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <button
                className="btn btn-outline"
                onClick={() => { setIsDetailModalOpen(false); setSelectedItem(null); }}
              >
                閉じる
              </button>
              {selectedItem.shipment?.status !== 'settled' && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setIsDetailModalOpen(false);
                    handleEdit(selectedItem);
                  }}
                >
                  <Edit size={16} />
                  編集
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setSelectedItem(null); }}
        title="出庫編集"
        size="md"
      >
        {selectedItem && (
          <form onSubmit={handleSubmitEdit} className="space-y-4">
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                {selectedItem.purchase?.productName}
              </p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                LOT: {selectedItem.shipment?.shipmentLot} | 出庫日: {selectedItem.shipment?.shipmentDate}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">数量(ケース)</label>
                <input
                  type="number"
                  className="input-field"
                  value={editForm.quantity}
                  onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })}
                />
              </div>
              <div>
                <label className="label">重量(kg)</label>
                <input
                  type="number"
                  step="0.01"
                  className="input-field"
                  value={editForm.weight}
                  onChange={(e) => setEditForm({ ...editForm, weight: e.target.value })}
                />
              </div>
              <div>
                <label className="label">売単価(円/kg)</label>
                <input
                  type="number"
                  step="0.01"
                  className="input-field"
                  value={editForm.unitPrice}
                  onChange={(e) => setEditForm({ ...editForm, unitPrice: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="label">着日</label>
              <input
                type="date"
                className="input-field"
                value={editForm.arrivalDate}
                onChange={(e) => setEditForm({ ...editForm, arrivalDate: e.target.value })}
              />
            </div>

            <div>
              <label className="label">備考</label>
              <textarea
                className="input-field"
                rows={2}
                value={editForm.remarks}
                onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { setIsEditModalOpen(false); setSelectedItem(null); }}
              >
                キャンセル
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={updateMutation.isPending}
              >
                更新
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
